import { zodResolver } from '@hookform/resolvers/zod'
import { LogIn, UserPlus, X } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Avatar, Button, Field, InlineAlert, Input } from '@aeris/ui'

import { AuthCard } from '../components/auth-card.tsx'
import { ApiError, ApiUnreachableError } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import {
  forgetAccount,
  readAccounts,
  type SavedAccount,
} from '../lib/saved-accounts.ts'

/**
 * Login screen.
 *
 * The credentials are verified by the API. Nothing here decides whether the
 * sign-in succeeded, and the session token never reaches this code: the server
 * sets an httpOnly cookie the browser cannot read. See ADR-0012.
 *
 * The client-side schema checks only that the fields are filled. Any rule
 * beyond that would either duplicate the server's or contradict it, and telling
 * someone their password is "invalid" before asking the server is a way to
 * leak which passwords a system considers well-formed.
 *
 * ## As contas deste aparelho (F153)
 *
 * A tela tem dois momentos: **escolher quem é** e **digitar a senha**. Qual
 * deles abre depende de quantas contas o aparelho já viu, e a regra foi
 * escolhida pelo caso comum de cada um:
 *
 * - **nenhuma** — formulário em branco, foco no e-mail. Não há o que escolher;
 * - **uma** — direto na senha, com o nome em cima. Quem abre a loja sozinha
 *   todo dia não deve pagar um clique por um problema que é de PC
 *   compartilhado;
 * - **duas ou mais** — a lista primeiro. Aqui o clique não é custo: é o que
 *   substitui digitar o endereço inteiro.
 */

const loginSchema = z.object({
  email: z.string().trim().min(1, 'Informe o e-mail').max(320),
  password: z.string().min(1, 'Informe a senha').max(256),
})

type LoginForm = z.infer<typeof loginSchema>

const messageFor = (error: unknown): string => {
  if (error instanceof ApiUnreachableError) {
    return 'Não foi possível falar com o servidor. Verifique sua conexão.'
  }
  if (error instanceof ApiError) {
    // The server deliberately answers the same way for an unknown address and
    // a wrong password, so this message says no more than it should.
    return error.message
  }
  return 'Não foi possível entrar. Tente novamente.'
}

/**
 * O que a pessoa reconhece como ela mesma.
 *
 * Uma conta migrada da F151 não tem nome — só foi guardado o e-mail. Mostrar
 * o endereço é honesto; inventar um nome a partir dele ("elias@loja" → "Elias")
 * acertaria às vezes e erraria com confiança o resto.
 */
const nameOf = (account: SavedAccount): string =>
  account.displayName === '' ? account.email : account.displayName

export const LoginPage = (): ReactNode => {
  const { signIn } = useAuth()
  const [formError, setFormError] = useState<string | null>(null)

  /*
   * As contas deste aparelho, lidas UMA vez na montagem.
   *
   * Em estado, e não lidas a cada render, porque remover uma conta tem de
   * mudar a tela na hora — e porque reler a cada render traria de volta o que
   * a pessoa acabou de apagar.
   */
  const [initial] = useState<readonly SavedAccount[]>(readAccounts)
  const [accounts, setAccounts] = useState(initial)
  const [chosen, setChosen] = useState<SavedAccount | null>(initial[0] ?? null)
  const [picking, setPicking] = useState(initial.length > 1)

  const {
    register,
    handleSubmit,
    setValue,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: chosen?.email ?? '', password: '' },
  })

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null)
    try {
      await signIn(values)
      // No navigation here: RedirectWhenSignedIn moves on once the session
      // exists, so there is one place that decides where a signed-in user goes.
    } catch (error) {
      setFormError(messageFor(error))
    }
  })

  /** Escolher alguém da lista: só falta a senha. */
  const choose = (account: SavedAccount): void => {
    setFormError(null)
    setChosen(account)
    setValue('email', account.email)
    setValue('password', '')
    setPicking(false)
    setFocus('password')
  }

  /** "Entrar com outra conta": formulário em branco, sem apagar ninguém. */
  const startBlank = (): void => {
    setFormError(null)
    setChosen(null)
    setValue('email', '')
    setValue('password', '')
    setPicking(false)
    setFocus('email')
  }

  const remove = (account: SavedAccount): void => {
    forgetAccount(account.email)
    const left = readAccounts()
    setAccounts(left)
    if (left.length === 0) {
      startBlank()
      return
    }
    // Tirar a conta que estava escolhida deixaria a senha apontando para
    // ninguém: a lista volta a ser a pergunta.
    if (chosen?.email === account.email) {
      setChosen(null)
      setValue('email', '')
      setPicking(true)
    }
  }

  if (picking) {
    return (
      <AuthCard>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h1 className="text-(length:--text-lg) font-(--weight-semibold) text-(--text-primary)">
              Quem está entrando?
            </h1>
            <p className="text-(length:--text-xs) text-(--text-muted)">
              Escolha sua conta para digitar só a senha.
            </p>
          </div>

          <ul aria-label="Contas deste computador" className="flex flex-col">
            {accounts.map((account) => (
              <li key={account.email} className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => {
                    choose(account)
                  }}
                  className="flex min-w-0 flex-1 items-center gap-3 rounded-(--radius-md) px-2 py-2 text-left hover:bg-(--surface-sunken)"
                >
                  <Avatar label={nameOf(account)} seed={account.email} />
                  <span className="min-w-0">
                    <span className="block truncate text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
                      {nameOf(account)}
                    </span>
                    {/* O e-mail repetido embaixo do nome não é enfeite: duas
                        pessoas da mesma família com o mesmo primeiro nome é
                        comum, e é o endereço que desempata. */}
                    {account.displayName === '' ? null : (
                      <span className="block truncate text-(length:--text-xs) text-(--text-muted)">
                        {account.email}
                      </span>
                    )}
                  </span>
                </button>
                <button
                  type="button"
                  aria-label={`Remover ${nameOf(account)} deste computador`}
                  onClick={() => {
                    remove(account)
                  }}
                  className="shrink-0 rounded-(--radius-md) p-2 text-(--text-muted) hover:text-(--text-primary)"
                >
                  <X aria-hidden="true" className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>

          <Button
            type="button"
            variant="ghost"
            leadingIcon={<UserPlus aria-hidden="true" className="h-4 w-4" />}
            onClick={startBlank}
          >
            Entrar com outra conta
          </Button>
        </div>
      </AuthCard>
    )
  }

  return (
    <AuthCard>
      <form
        onSubmit={(event) => {
          void onSubmit(event)
        }}
        noValidate
        className="flex flex-col gap-4"
      >
        <div className="flex flex-col gap-1">
          <h1 className="text-(length:--text-lg) font-(--weight-semibold) text-(--text-primary)">
            Entrar
          </h1>
          <p className="text-(length:--text-xs) text-(--text-muted)">
            Informe suas credenciais para acessar o sistema.
          </p>
        </div>

        {/* Announced on appearance: a submit failure that only changes colour
              is invisible to a screen reader and easy to miss on a busy
              counter. */}
        {formError === null ? null : (
          <InlineAlert tone="danger">{formError}</InlineAlert>
        )}

        {chosen === null ? (
          <Field label="E-mail" required error={errors.email?.message}>
            {(field) => (
              <Input
                {...field}
                {...register('email')}
                type="email"
                autoFocus
                autoComplete="username"
                placeholder="voce@empresa.com.br"
              />
            )}
          </Field>
        ) : (
          <div className="flex flex-col gap-2">
            {/*
              Quem já está escolhido aparece como PESSOA, não como campo de
              texto: o endereço continua ali para conferir, e o campo some
              porque não há o que preencher nele.

              O e-mail continua sendo enviado mesmo sem campo montado: o
              react-hook-form guarda o valor (`shouldUnregister` é `false` por
              padrão), e `choose` o grava com `setValue`. Um `<input hidden>`
              aqui pareceria a peça que segura isso, e não seria — a sabotagem
              de tirá-lo não deixou nenhum teste vermelho. Quem segura é o
              teste "o e-mail continua sendo mandado, mesmo sem campo na tela".

              O `<input hidden>` do register continua mandando o e-mail — tirar
              o campo da tela não pode tirar o dado do formulário.
            */}
            <div className="flex items-center gap-3 rounded-(--radius-md) bg-(--surface-sunken) px-3 py-2">
              <Avatar label={nameOf(chosen)} seed={chosen.email} />
              <div className="min-w-0">
                <p className="truncate text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
                  {nameOf(chosen)}
                </p>
                {chosen.displayName === '' ? null : (
                  <p className="truncate text-(length:--text-xs) text-(--text-muted)">
                    {chosen.email}
                  </p>
                )}
              </div>
            </div>

            {/*
              O PC do balcão é de mais de uma pessoa (F151/F153).
              Com duas ou mais contas, voltar é escolher outra cara; com uma
              só, é começar do zero. O rótulo diz qual dos dois acontece.
            */}
            <button
              type="button"
              className="self-start text-(length:--text-xs) text-(--text-muted) underline hover:text-(--text-primary)"
              onClick={() => {
                if (accounts.length > 1) {
                  setPicking(true)
                  return
                }
                startBlank()
              }}
            >
              {accounts.length > 1
                ? 'Trocar de usuário'
                : 'Não é você? Entrar com outra conta'}
            </button>
          </div>
        )}

        <Field label="Senha" required error={errors.password?.message}>
          {(field) => (
            <Input
              {...field}
              {...register('password')}
              type="password"
              // O foco vai para o campo que ainda falta preencher.
              autoFocus={chosen !== null}
              autoComplete="current-password"
              placeholder="••••••••"
            />
          )}
        </Field>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={isSubmitting}
          leadingIcon={<LogIn aria-hidden="true" className="h-4 w-4" />}
          className="mt-1 w-full"
        >
          Entrar
        </Button>
      </form>
    </AuthCard>
  )
}
